import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Calculator, 
  FileText, 
  Clock,
  CheckCircle,
  TrendingUp,
  Target,
  Calendar,
  BookOpen,
  ArrowUpRight,
  Plus
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge } from '@plvplus/core';
import { User } from '../../hooks/useUserRoles';
import { PriPlusOverview } from './widgets/PriPlusOverview';

interface UserDashboardProps {
  user: User;
}

export const UserDashboard: React.FC<UserDashboardProps> = ({ user }) => {
  // Mock user data
  const personalStats = [
    {
      name: 'Calculs ce mois',
      value: '12',
      change: '+3',
      changeType: 'positive' as const,
      icon: Calculator,
      description: 'Terminés'
    },
    {
      name: 'Devis créés',
      value: '8',
      change: '+2',
      changeType: 'positive' as const,
      icon: FileText,
      description: 'Ce mois'
    },
    {
      name: 'Taux validation',
      value: '85%',
      change: '+5%',
      changeType: 'positive' as const,
      icon: CheckCircle,
      description: 'Mes calculs'
    },
    {
      name: 'Objectif mensuel',
      value: '67%',
      change: 'En cours',
      changeType: 'neutral' as const,
      icon: Target,
      description: '€15K / €22K'
    },
    {
      name: 'Retards',
      value: '3',
      change: '+1',
      changeType: 'negative' as const,
      icon: Clock,
      description: 'Projets en retard'
    }
  ];

  const myTasks = [
    {
      id: '1',
      title: 'Finaliser calcul PLV Pharmacie',
      description: 'Présentoir cosmétiques - Client Martin',
      dueDate: 'Aujourd\'hui',
      priority: 'high',
      status: 'in_progress'
    },
    {
      id: '2',
      title: 'Réviser devis Carrefour',
      description: 'Gondole parfums - Demande de modification',
      dueDate: 'Demain',
      priority: 'medium',
      status: 'pending'
    },
    {
      id: '3',
      title: 'Formation PRI+ avancé',
      description: 'Module calculs complexes',
      dueDate: 'Vendredi',
      priority: 'low',
      status: 'scheduled'
    }
  ];

  const recentWork = [
    {
      id: '1',
      type: 'calculation',
      title: 'PLV Bio&Co - Stand produits bio',
      status: 'completed',
      amount: '€1,147.50',
      time: '2h'
    },
    {
      id: '2',
      type: 'quote',
      title: 'Devis Pharmacie Central',
      status: 'sent',
      amount: '€2,450.00',
      time: '1j'
    },
    {
      id: '3',
      type: 'calculation',
      title: 'Présentoir Leclerc - Rayonnage',
      status: 'draft',
      amount: '€890.00',
      time: '2j'
    }
  ];

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'border-l-red-500 bg-red-50 dark:bg-red-900/10';
      case 'medium': return 'border-l-yellow-500 bg-yellow-50 dark:bg-yellow-900/10';
      default: return 'border-l-blue-500 bg-blue-50 dark:bg-blue-900/10';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case 'sent': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed': return 'Terminé';
      case 'sent': return 'Envoyé';
      case 'in_progress': return 'En cours';
      case 'draft': return 'Brouillon';
      case 'pending': return 'En attente';
      case 'scheduled': return 'Planifié';
      default: return status;
    }
  };

  return (
    <div className="space-y-6">
      {/* User Header */}
      <div className="bg-gradient-to-r from-green-600 to-teal-600 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">
              Bonjour {user.name.split(' ')[0]} ! 🎯
            </h1>
            <p className="text-green-100">
              Voici votre espace de travail personnalisé. Continuez votre excellent travail !
            </p>
          </div>
          <div className="hidden md:block">
            <div className="text-right">
              <p className="text-sm text-green-200">Votre rôle</p>
              <Badge variant="secondary" className="bg-white/20 text-white border-white/30">
                {user.roles[0]?.name || 'Utilisateur'}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Personal Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {personalStats.map((stat) => (
          <Card key={stat.name} variant="elevated">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    {stat.name}
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {stat.value}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {stat.description}
                  </p>
                </div>
                <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-full">
                  <stat.icon className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
              </div>
              <div className="mt-4">
                <span className={`text-sm font-medium ${
                  stat.changeType === 'positive' 
                    ? 'text-green-600 dark:text-green-400' 
                    : stat.changeType === 'negative'
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-gray-600 dark:text-gray-400'
                }`}>
                  {stat.change}
                </span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* PRI+ Overview */}
        <div className="lg:col-span-2">
          <PriPlusOverview compact={false} showQuickActions={true} />
        </div>

        {/* My Tasks */}
        <Card variant="elevated">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center space-x-2">
                <Clock className="h-5 w-5" />
                <span>Mes tâches</span>
              </CardTitle>
              <Badge variant="secondary">{myTasks.length}</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {myTasks.map((task) => (
                <div
                  key={task.id}
                  className={`border-l-4 rounded-r-lg p-4 transition-all hover:shadow-sm ${getPriorityColor(task.priority)}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                        {task.title}
                      </h4>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        {task.description}
                      </p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Échéance: {task.dueDate}
                        </span>
                        <Badge 
                          variant="outline" 
                          className={getStatusColor(task.status)}
                        >
                          {getStatusLabel(task.status)}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4">
              <Button variant="outline" size="sm" className="w-full">
                Voir toutes mes tâches
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Recent Work */}
        <Card variant="elevated">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center space-x-2">
                <TrendingUp className="h-5 w-5" />
                <span>Travail récent</span>
              </CardTitle>
              <Button variant="outline" size="sm">
                Historique
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentWork.map((work) => (
                <div key={work.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-primary-100 dark:bg-primary-900/20 rounded-lg">
                      {work.type === 'calculation' ? (
                        <Calculator className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                      ) : (
                        <FileText className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                        {work.title}
                      </h4>
                      <div className="flex items-center space-x-2 mt-1">
                        <Badge 
                          variant="outline" 
                          className={getStatusColor(work.status)}
                        >
                          {getStatusLabel(work.status)}
                        </Badge>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Il y a {work.time}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                      {work.amount}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* User Quick Actions */}
      <Card variant="elevated">
        <CardHeader>
          <CardTitle>Actions rapides</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link
              to="/pri-plus"
              className="group block p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-all hover:border-green-300 dark:hover:border-green-600"
            >
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-500 rounded-lg">
                  <Calculator className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-green-600 dark:group-hover:text-green-400">
                    Nouveau calcul
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    PRI+ Prix de revient
                  </p>
                </div>
                <ArrowUpRight className="h-4 w-4 text-gray-400 group-hover:text-green-600 dark:group-hover:text-green-400" />
              </div>
            </Link>

            <div className="group block p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-all hover:border-green-300 dark:hover:border-green-600 cursor-pointer">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-500 rounded-lg">
                  <FileText className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-green-600 dark:group-hover:text-green-400">
                    Créer devis
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    À partir d'un calcul
                  </p>
                </div>
                <ArrowUpRight className="h-4 w-4 text-gray-400 group-hover:text-green-600 dark:group-hover:text-green-400" />
              </div>
            </div>

            <div className="group block p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-all hover:border-green-300 dark:hover:border-green-600 cursor-pointer">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-500 rounded-lg">
                  <Calendar className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-green-600 dark:group-hover:text-green-400">
                    Mon planning
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Tâches et RDV
                  </p>
                </div>
                <ArrowUpRight className="h-4 w-4 text-gray-400 group-hover:text-green-600 dark:group-hover:text-green-400" />
              </div>
            </div>

            <div className="group block p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-all hover:border-green-300 dark:hover:border-green-600 cursor-pointer">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-orange-500 rounded-lg">
                  <BookOpen className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-green-600 dark:group-hover:text-green-400">
                    Formation
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Tutoriels PLV+
                  </p>
                </div>
                <ArrowUpRight className="h-4 w-4 text-gray-400 group-hover:text-green-600 dark:group-hover:text-green-400" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
